import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import SideBar from 'Components/SideBar';
import Pagination from 'Components/Pagination';
import CommonModal from 'Components/CommonModal';
import SubscriptionDetailModal from 'Components/SubscriptionDetailModal';
import SubscriptionExtendModal from 'Components/SubscriptionExtendModal';
import SubscriptionGrantModal from 'Components/SubscriptionGrantModal';
import {
  catchError,
  changeState,
  commonModalSetting,
  enterFn,
  maskingInfo,
} from 'JS/common';
import { getSubscriptions, searchSubscriptionUser } from 'JS/API';
import { statusArr, subscriptionTypeArr } from 'JS/array';

const Subscription = () => {
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    totalPage: 1,
    limit: 10,
  });
  const [list, setList] = useState([]);
  const [alert, setAlert] = useState('');
  const [alertBox, setAlertBox] = useState({
    mode: '',
    context: '',
    bool: false,
  });

  // 필터 상태
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTarget, setSearchTarget] = useState('name');
  const [searchTxt, setSearchTxt] = useState('');
  const [searchedUserPk, setSearchedUserPk] = useState(null);

  // 모달 상태
  const [detailModal, setDetailModal] = useState(false);
  const [extendModal, setExtendModal] = useState(false);
  const [grantModal, setGrantModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const navigate = useNavigate();
  let prevent = false;

  // 이용권 목록 불러오기
  const getList = async (userPk = null) => {
    if (prevent) return;
    prevent = true;
    setTimeout(() => {
      prevent = false;
    }, 200);

    const params = {
      page: pageInfo.page,
      limit: pageInfo.limit,
      status: statusFilter,
      type: typeFilter,
    };
    if (userPk) params.user_pk = userPk;

    const result = await getSubscriptions(params);
    if (typeof result === 'object') {
      const subscriptions = result?.data?.data || [];
      setList(subscriptions);
      changeState(setPageInfo, 'totalPage', result?.data?.meta?.total_pages || 1);
    } else return catchError(result, navigate, setAlertBox, setAlert);
  };

  // 회원 검색 후 이용권 조회
  const handleSearch = async () => {
    if (!searchTxt.trim()) {
      setSearchedUserPk(null);
      changeState(setPageInfo, 'page', 1);
      getList();
      return;
    }

    const result = await searchSubscriptionUser(searchTarget, searchTxt, 1, 10);
    if (typeof result === 'object') {
      const users = result?.data?.data || [];
      if (users.length > 0) {
        const userPk = users[0].user_pk;
        setSearchedUserPk(userPk);
        changeState(setPageInfo, 'page', 1);
        getList(userPk);
      } else {
        commonModalSetting(
          setAlertBox,
          true,
          'alert',
          '검색 결과가 없습니다.'
        );
      }
    } else return catchError(result, navigate, setAlertBox, setAlert);
  };

  // 필터 초기화
  const resetFilters = () => {
    setStatusFilter('all');
    setTypeFilter('all');
    setSearchTxt('');
    setSearchedUserPk(null);
    changeState(setPageInfo, 'page', 1);
  };

  // 날짜 포맷팅
  const formatDate = dateStr => {
    if (!dateStr) return '-';
    return dateStr.replace('T', ' ').substring(0, 10);
  };

  // 행 클릭 - 상세 모달
  const handleRowClick = item => {
    setSelectedItem(item);
    setDetailModal(true);
  };

  // 연장 버튼 클릭
  const handleExtendClick = (e, item) => {
    e.stopPropagation();
    setSelectedItem(item);
    setExtendModal(true);
  };

  // 환불 버튼 클릭 - 서비스 준비중
  const handleRefundClick = e => {
    e.stopPropagation();
    commonModalSetting(setAlertBox, true, 'alert', '서비스 준비 중입니다.');
  };

  // 테이블 렌더링
  const renderTableBody = () => {
    if (!list || list.length === 0) {
      return (
        <tr>
          <td colSpan='8' className='none-list'>
            목록이 없습니다.
          </td>
        </tr>
      );
    }

    return list.map((item, index) => {
      const {
        user_pk,
        user_name,
        user_id,
        merchant_name,
        target_type,
        status,
        start_at,
        end_at,
      } = item;

      // 고유 키 (subscription_id, id, 또는 index 사용)
      const itemKey = item.subscription_id || item.id || index;

      // 소문자로 오는 status 값 비교
      const isActive = status === 'active';
      const isOrder = target_type === 'ORDER';

      return (
        <tr key={itemKey} onClick={() => handleRowClick(item)} className='clickable'>
          <td>{user_name ? maskingInfo('name', user_name) : user_pk}</td>
          <td>{user_id || '-'}</td>
          <td>{merchant_name || '-'}</td>
          <td>{subscriptionTypeArr[target_type] || target_type}</td>
          <td>{formatDate(start_at)}</td>
          <td>{formatDate(end_at)}</td>
          <td className={isActive ? 'active-status' : 'inactive-status'}>
            {statusArr[status] || status}
          </td>
          <td className='action-btns'>
            {isActive && (
              <button
                className='extend-btn'
                onClick={e => handleExtendClick(e, item)}>
                연장
              </button>
            )}
            {isOrder && isActive && (
              <button
                className='refund-btn'
                onClick={e => handleRefundClick(e, item)}>
                환불
              </button>
            )}
          </td>
        </tr>
      );
    });
  };

  useEffect(() => {
    document.title = '마크클라우드 관리자 > 쿠폰/이용권 관리';
  }, []);

  useEffect(() => {
    if (!detailModal && !extendModal && !grantModal) {
      if (searchedUserPk) {
        getList(searchedUserPk);
      } else {
        getList();
      }
    }
  }, [
    pageInfo.page,
    pageInfo.limit,
    statusFilter,
    typeFilter,
    detailModal,
    extendModal,
    grantModal,
  ]);

  return (
    <>
      <div className='container'>
        <SideBar />
        <div className='content-wrap subscription'>
          <div className='topBar'>
            <h2>쿠폰/이용권 관리</h2>
            <div className='filter-row'>
              <select
                value={pageInfo.limit}
                onChange={e =>
                  setPageInfo(prev => ({
                    ...prev,
                    page: 1,
                    limit: Number(e.target.value),
                  }))
                }>
                <option value={10}>10개씩 보기</option>
                <option value={30}>30개씩 보기</option>
                <option value={50}>50개씩 보기</option>
              </select>
              <select
                value={statusFilter}
                onChange={e => {
                  setStatusFilter(e.target.value);
                  changeState(setPageInfo, 'page', 1);
                }}>
                <option value='all'>상태: 전체</option>
                <option value='pending'>대기중</option>
                <option value='active'>사용중</option>
                <option value='expired'>만료</option>
                <option value='refunded'>환불</option>
                <option value='cancelled'>취소</option>
              </select>
              <select
                value={typeFilter}
                onChange={e => {
                  setTypeFilter(e.target.value);
                  changeState(setPageInfo, 'page', 1);
                }}>
                <option value='all'>유형: 전체</option>
                <option value='order'>결제</option>
                <option value='event'>이벤트</option>
                <option value='admin_defined'>관리자 부여</option>
              </select>
              <select
                value={searchTarget}
                onChange={e => setSearchTarget(e.target.value)}>
                <option value='name'>이름</option>
                <option value='email'>이메일</option>
                <option value='id'>아이디</option>
              </select>
              <input
                type='text'
                placeholder='회원 검색'
                value={searchTxt}
                onChange={e => setSearchTxt(e.target.value)}
                onKeyDown={e => {
                  if (!alertBox.bool) enterFn(e, handleSearch);
                }}
              />
              <button className='searchBtn' onClick={handleSearch}>
                검색
                <span className='searchIcon'>
                  <FaSearch />
                </span>
              </button>
              <button className='resetBtn' onClick={resetFilters}>
                초기화
              </button>
              <button className='grantBtn' onClick={() => setGrantModal(true)}>
                + 신규 발급
              </button>
            </div>
          </div>
          <div className='table-wrap'>
            <table>
              <colgroup>
                <col width='10%' />
                <col width='15%' />
                <col width='18%' />
                <col width='10%' />
                <col width='12%' />
                <col width='12%' />
                <col width='8%' />
                <col width='15%' />
              </colgroup>
              <thead>
                <tr>
                  <th>회원명</th>
                  <th>아이디</th>
                  <th>이용권명</th>
                  <th>유형</th>
                  <th>시작일</th>
                  <th>만료일</th>
                  <th>상태</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </table>
          </div>
          {pageInfo.totalPage > 1 && (
            <Pagination setPageInfo={setPageInfo} pageInfo={pageInfo} />
          )}
        </div>
        {alertBox.bool && (
          <CommonModal
            setModal={setAlertBox}
            modal={alertBox}
            okFn={() => {
              if (alert === 'logout') navigate('/');
              else return;
            }}
          />
        )}
      </div>
      {detailModal && selectedItem && (
        <SubscriptionDetailModal
          setModal={setDetailModal}
          item={selectedItem}
          setExtendModal={setExtendModal}
          setAlertBox={setAlertBox}
        />
      )}
      {extendModal && selectedItem && (
        <SubscriptionExtendModal
          setModal={setExtendModal}
          item={selectedItem}
          setAlertBox={setAlertBox}
          setAlert={setAlert}
        />
      )}
      {grantModal && (
        <SubscriptionGrantModal
          setModal={setGrantModal}
          setAlertBox={setAlertBox}
          setAlert={setAlert}
        />
      )}
    </>
  );
};

export default Subscription;